import { useState, useCallback } from 'react'
import { ChevronDown } from 'lucide-react'
import clsx from 'clsx'

type Unit = {
  id: string;
  area: number; // Storing as number for sorting
  building: string;
  floor: number;
  rooms: number;
};

const mainApartments = [
  { id: 'A1', area: 119, rooms: 3, floors: [1], price: 280000, apt_plans: '/apt_plans/base_1.webp' },
  { id: 'A2', area: 99, rooms: 2, floors: [1], price: 285000, apt_plans: '/apt_plans/base_2.webp' },
  { id: 'A3', area: 89, rooms: 2, floors: [1], price: 270000, apt_plans: '/apt_plans/base_3.webp' },

  { id: 'B1', area: 90, rooms: 2, floors: [2, 3, 4], price: 280000, apt_plans: '/apt_plans/middle_1.webp' },
  { id: 'B2', area: 52, rooms: 1, floors: [2, 3, 4], price: 215000, apt_plans: '/apt_plans/middle_2.webp' },
  { id: 'B3', area: 86, rooms: 2, floors: [2, 3, 4], price: 295000, apt_plans: '/apt_plans/middle_3.webp' },
  { id: 'B4', area: 85, rooms: 2, floors: [2, 3, 4], price: 300000, apt_plans: '/apt_plans/middle_4.webp' },

  { id: 'C1', area: 112, rooms: 3, floors: [5], price: 420000, apt_plans: '/apt_plans/top_1.webp' },
  { id: 'C2', area: 107, rooms: 3, floors: [5], price: 445000, apt_plans: '/apt_plans/top_2.webp' },
]

// Generate units for all apartments across all floors (6 floors total)
const generateAllUnits = (): Unit[] => {
  const units: Unit[] = []

  // Generate units by apartment group first, then by their specific floors
  mainApartments.forEach(apt => {
    apt.floors.forEach(floor => {
      const unitId = `${apt.id}-${floor}`
      
      // Skip A1-1 apartment (make it unavailable)
      if (unitId === 'A1-1') {
        return
      }
      if (unitId === 'A3-1') {
        return
      }

      if (unitId === 'B1-2' || unitId === 'B1-3' ||

        unitId === 'B2-2' ||unitId === 'B2-3' ||unitId === 'B2-4' ||

        unitId === 'B3-2' ||unitId === 'B3-4' ||
        unitId === 'B4-2' ||unitId === 'B4-3'

      ) {
        return
      }
      
      units.push({
        id: `${apt.id}-${floor}`,
        area: apt.area,
        building: 'A',
        floor: floor,
        rooms: apt.rooms
      })
    })
  })

  return units
}

export const initialUnits: Unit[] = generateAllUnits()
export { mainApartments }

type SortKey = keyof Unit;
type SortDirection = 'asc' | 'desc';

type UnitListProps = {
  onApartmentHover?: (apartmentInfo: { id: string; floor: number } | null) => void;
  onApartmentClick?: (apartmentInfo: { id: string; floor: number }) => void;
};

export function UnitList ({ onApartmentHover, onApartmentClick }: UnitListProps): JSX.Element {
  const [units, setUnits] = useState(initialUnits)
  const [sortKey, setSortKey] = useState<SortKey>('id')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  // Memoize handlers to prevent unnecessary re-renders
  const handleMouseEnter = useCallback((unit: Unit) => {
    onApartmentHover?.({ id: unit.id.split('-')[0], floor: unit.floor })
  }, [onApartmentHover])

  const handleMouseLeave = useCallback(() => {
    onApartmentHover?.(null)
  }, [onApartmentHover])

  const handleClick = useCallback((unit: Unit) => {
    onApartmentClick?.({ id: unit.id.split('-')[0], floor: unit.floor })
  }, [onApartmentClick])

  const handleSort = (key: SortKey) => {
    const newDirection = sortKey === key && sortDirection === 'asc' ? 'desc' : 'asc'
    setSortKey(key)
    setSortDirection(newDirection)

    const sorted = [...units].sort((a, b) => {
      const aVal = a[key]
      const bVal = b[key]
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return newDirection === 'asc' ? aVal - bVal : bVal - aVal
      } else {
        return newDirection === 'asc'
          ? String(aVal).localeCompare(String(bVal))
          : String(bVal).localeCompare(String(aVal))
      }
    })

    setUnits(sorted)
  }

  const headers: { label: string; key: SortKey }[] = [
    { label: 'ID', key: 'id' },
    { label: 'Area', key: 'area' },
    { label: 'Rooms', key: 'rooms' },
    { label: 'Floor', key: 'floor' },
  ]

  return (
    <div className="bg-white text-black w-full rounded-xl overflow-hidden shadow-xl flex flex-col h-full">
      {/* ─────────── HEADER ─────────── */}
      <div className="flex-none">
        <table className="w-full table-fixed text-sm">
          <colgroup>
            <col className="w-[30%]"/>
            <col className="w-[30%]"/>
            <col className="w-[20%]"/>
            <col className="w-[20%]"/>
          </colgroup>
          <thead className="bg-white">
          <tr>
            {headers.map((h) => (
              <th
                key={h.key}
                className="cursor-pointer text-gray-400 hover:text-black py-2 pl-2"
                onClick={() => handleSort(h.key)}
              >
                <div className="flex items-center gap-1">
                  {h.label}
                  {sortKey === h.key && (
                    <ChevronDown
                      className={clsx('transition-transform', {
                        'rotate-180': sortDirection === 'asc',
                      })}
                      size={14}
                    />
                  )}
                </div>
              </th>
            ))}
          </tr>
          </thead>
        </table>
      </div>

      {/* ─────────── BODY ─────────── */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <table className="w-full table-fixed text-sm">
          <colgroup>
            <col className="w-[30%]"/>
            <col className="w-[30%]"/>
            <col className="w-[20%]"/>
            <col className="w-[20%]"/>
          </colgroup>
          <tbody className="divide-y">
          {units.map((u) => (
            <tr
              key={u.id}
              className="transition-colors duration-150 hover:bg-gray-200 bg-gray-50"
              onMouseEnter={() => handleMouseEnter(u)}
              onMouseLeave={handleMouseLeave}
              onClick={() => handleClick(u)}
            >
              <td className="px-3 py-2 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: '#0080ff' }}/>
                {u.id}
              </td>
              <td className="px-3 py-2">{u.area.toFixed(2)}m²</td>
              <td className="px-4 py-2">{u.rooms}</td>
              <td className="px-5 py-2">{u.floor}</td>
            </tr>
          ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
